import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, notification, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import VersionHistoryFooter from '../../../foundation/components/version_history_footer/VersionHistoryFooter';
import {
  BRAND_PRIMARY,
  PATHOGENIC_RED,
  SUCCESS_GREEN,
} from '../../../foundation/config/colors';
import { boldColValueRenderer } from '../../../foundation/utils/boldColValueRenderer';
import { useGetPredictionToolsQuery } from '../../api/globalDataApi';
import {
  useGetSpliceSiteAlterationQuery,
  useGetVariantLinkoutsQuery,
  useGetVariantPredictionToolsQuery,
  useUpdateSilicoMainDataMutation,
  useUpdateSilicoSpliceSiteAlterationMutation,
} from '../../api/variantApi';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import CollapsePanel from '../components/CollapsePanel';
import DetailCard from '../components/DetailCard';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { VARIANT_STAGE_TYPES } from '../constants';
import { DATA_SOURCE_FALLBACKS } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useHasRunningRefresh } from '../hooks/useHasRunningRefresh';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { SilicoPredictionType } from '../redux';
import { groupToolsByCategory } from '../redux/reducerUtils';
import SilicoPredictionToolEditModal from './SilicoPredictionToolEditModal';
import SilicoToolModal from './SilicoToolModal';

const GREEN_COLOR = SUCCESS_GREEN;
const RED_COLOR = PATHOGENIC_RED;

interface SpliceData {
  deltaScore: number;
  altScore: number;
  refScore: number;
  type: string;
  position: number;
}

const SilicoPredictionSection = () => {
  const params = useParams();

  // BIOFRO-80 ABS review: use the mutation hook's built-in isLoading instead
  // of separate useState flags.
  const [updateSilicoMainData, { isLoading: isMainTableLoading }] =
    useUpdateSilicoMainDataMutation();
  const [
    updateSilicoSpliceSiteAlteration,
    { isLoading: isSliceAlterationTableLoading },
  ] = useUpdateSilicoSpliceSiteAlterationMutation();

  const [isExpanded, setIsExpanded] = useState(false);

  // new modal state
  const [isToolModalVisible, setIsToolModalVisible] = useState(false);
  const handleToolModalClose = () => setIsToolModalVisible(false);

  const [isToolEditModalVisible, setIsToolEditModalVisible] = useState(false);
  const [selectedTool, setSelectedTool] = useState<
    (SilicoPredictionType & { id?: number }) | null
  >(null);
  const { data: predictionTools = [] } = useGetPredictionToolsQuery();

  // Read predictions straight from the query hook's cache rather than a selector
  const { data: variantPredictionToolsResponse } =
    useGetVariantPredictionToolsQuery(
      { variant_id: params.vid },
      { skip: !params.vid },
    );
  const variantPredictionToolsPayload = variantPredictionToolsResponse || {};
  const tools = variantPredictionToolsPayload.tools || [];
  const {
    functional: functionalPrediction,
    conservation: conservationPrediction,
    aminoAcidDistance: aminoAcidDistancePrediction,
  } = groupToolsByCategory(tools);
  const silicoPredictionVersioningInfo = {
    source_version: variantPredictionToolsPayload.source_version,
    date: variantPredictionToolsPayload.last_update_vep_data,
  };

  // Read splice-site data straight from the query hook's cache
  const { data: spliceSiteAlterationPrediction } =
    useGetSpliceSiteAlterationQuery(
      { variant_id: params.vid },
      { skip: !params.vid },
    );

  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  const { canEditWorkflow } = useVariantAccess();
  const hasRunningRefresh = useHasRunningRefresh(params.vid);

  // Get stage info for disable logic
  const { stageType } = useParams();
  const stageInfo = useVariantStep(
    stageType || VARIANT_STAGE_TYPES.MOLECULAR_IMPACT,
  );
  // Disable "New tool" button if no user or stage is accepted
  const isNewToolDisabled =
    !canEditWorkflow || hasRunningRefresh || stageInfo?.status === 'accepted';
  // Disable update data links in version history footer if no user or stage is accepted or variant is locked
  const isUpdateDisabled =
    !canEditWorkflow || hasRunningRefresh || stageInfo?.status === 'accepted';

  // Excluded tools moved from backend to frontend filtering
  const EXCLUDED_TOOLS = useMemo(
    () => new Set(['MutationTaster', 'SpliceAI']),
    [],
  );

  // Filtered predictions (preserve original order)
  const filteredFunctionalPrediction = useMemo(
    () =>
      (functionalPrediction || []).filter((p) => !EXCLUDED_TOOLS.has(p.tool)),
    [functionalPrediction, EXCLUDED_TOOLS],
  );
  const filteredConservationPrediction = useMemo(
    () =>
      (conservationPrediction || []).filter((p) => !EXCLUDED_TOOLS.has(p.tool)),
    [conservationPrediction, EXCLUDED_TOOLS],
  );
  const filteredAminoAcidDistancePrediction = useMemo(
    () =>
      (aminoAcidDistancePrediction || []).filter(
        (p) => !EXCLUDED_TOOLS.has(p.tool),
      ),
    [aminoAcidDistancePrediction, EXCLUDED_TOOLS],
  );

  // Compact table data: take the first entry of each category (plus splice site alteration summary)
  const baseData = useMemo(() => {
    const data = [];

    // Add first functional prediction with category
    if (filteredFunctionalPrediction?.length > 0) {
      data.push({
        ...filteredFunctionalPrediction[0],
        category: 'Functional',
        key: '1',
      });
    }

    // Add first conservation prediction with category
    if (filteredConservationPrediction?.length > 0) {
      data.push({
        ...filteredConservationPrediction[0],
        category: 'Conservation',
        key: '2',
      });
    }

    // Add first amino acid distance prediction with category
    if (filteredAminoAcidDistancePrediction?.length > 0) {
      data.push({
        ...filteredAminoAcidDistancePrediction[0],
        category: 'Amino acid distance',
        key: '3',
      });
    }

    // Add splice site alteration prediction (excluding raw_scores)
    if (spliceSiteAlterationPrediction) {
      const { raw_scores, ...splicePrediction } =
        spliceSiteAlterationPrediction;
      data.push({
        ...splicePrediction,
        category: 'Splice site alteration',
        key: '4',
      });
    }

    return data;
  }, [
    filteredFunctionalPrediction,
    filteredConservationPrediction,
    filteredAminoAcidDistancePrediction,
    spliceSiteAlterationPrediction,
  ]);

  // Render calibrated prediction with color coding: red for pathogenic, green for benign
  const renderCalibratedPrediction = (
    text: string,
    record: SilicoPredictionType,
  ) => {
    const value = text;
    let color = BRAND_PRIMARY;

    if (value && value.includes('pathogenic')) {
      color = RED_COLOR;
    } else if (value && value.includes('benign')) {
      color = GREEN_COLOR;
    }

    return <span style={{ color }}>{value}</span>;
  };

  // Generic prediction rendering; SpliceAI displays its type + site alteration since field names differ
  const renderPrediction = (text: string, record: SilicoPredictionType) => {
    // For SpliceAI, we need to show the spliceai_type and splice_site_alteration
    // because the BE is not sending the same keys for all categories.
    if (record.tool === 'SpliceAI') {
      const spliceaiType = record.spliceai_type || '';
      const siteAlteration = record.splice_site_alteration || '';
      // Concatinate both values & add the comma only if both values exist.
      // If spliceai_type is empty, only siteAlteration is shown, with no leading comma.
      return (
        <span>
          {spliceaiType}
          {spliceaiType && siteAlteration ? ', ' : ''}
          {siteAlteration}
        </span>
      );
    }
    return <span>{text}</span>;
  };

  const handleToolClick = (record: SilicoPredictionType & { id?: number }) => {
    setSelectedTool(record);
    setIsToolEditModalVisible(true);
  };

  const handleToolEditModalClose = () => {
    setIsToolEditModalVisible(false);
    setSelectedTool(null);
  };

  // Main table columns; clicking a tool name opens edit modal;
  const columns: ColumnsType<any> = [
    {
      title: '',
      dataIndex: 'category',
      key: 'category',
      width: '170px',
      render: boldColValueRenderer,
    },
    {
      title: 'Tool',
      dataIndex: 'tool',
      key: 'tool',
      width: '230px',
      render: (value: string, record: any) => {
        const predictionTool = predictionTools.find(
          (tool) => tool.name === value,
        );
        const description = (
          <p>
            Source and version: {predictionTool?.source_version}
            <br />
            <br />
            Description: {predictionTool?.description}
            <br />
            Score range: {predictionTool?.score_range}
          </p>
        );

        return (
          <Tooltip
            title={description}
            overlayClassName="molecular-impact__tooltip"
            placement="bottom"
          >
            <a
              className="clinical-obs__report-category-link"
              onClick={() => handleToolClick(record)}
            >
              {value}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
    },
    {
      title: 'Prediction',
      dataIndex: 'prediction',
      key: 'prediction',
      render: renderPrediction,
    },
    {
      title: 'Calibrated prediction',
      dataIndex: 'calibrated_prediction',
      key: 'calibrated_prediction',
      render: renderCalibratedPrediction,
    },
  ];

  // Expanded table data: include all tools in each category and show splice-site details table below
  const expandedData = useMemo(() => {
    const data = [];
    let currentKey = 1;

    // Add all functional predictions
    if (filteredFunctionalPrediction?.length > 0) {
      filteredFunctionalPrediction.forEach((prediction, index) => {
        if (index === 0 || prediction.score !== null) {
          data.push({
            ...prediction,
            category: index === 0 ? 'Functional' : undefined, // Only add category for first item
            key: String(currentKey++),
          });
        }
      });
    }

    // Add all conservation predictions
    if (filteredConservationPrediction?.length > 0) {
      filteredConservationPrediction.forEach((prediction, index) => {
        data.push({
          ...prediction,
          category: index === 0 ? 'Conservation' : undefined,
          key: String(currentKey++),
        });
      });
    }

    // Add all amino acid distance predictions
    if (filteredAminoAcidDistancePrediction?.length > 0) {
      filteredAminoAcidDistancePrediction.forEach((prediction, index) => {
        data.push({
          ...prediction,
          category: index === 0 ? 'Amino acid distance' : undefined,
          key: String(currentKey++),
        });
      });
    }

    // Add splice site alteration prediction (excluding raw_scores)
    if (spliceSiteAlterationPrediction) {
      const { raw_scores, ...splicePrediction } =
        spliceSiteAlterationPrediction;
      data.push({
        ...splicePrediction,
        category: 'Splice site alteration',
        key: String(currentKey++),
      });
    }

    return data;
  }, [
    filteredFunctionalPrediction,
    filteredConservationPrediction,
    filteredAminoAcidDistancePrediction,
    spliceSiteAlterationPrediction,
  ]);

  const spliceColumns: ColumnsType<SpliceData> = [
    {
      title: 'Delta score',
      dataIndex: 'delta_score',
      key: 'delta_score',
    },
    {
      title: 'Alt score',
      dataIndex: 'alt_score',
      key: 'alt_score',
    },
    {
      title: 'Ref score',
      dataIndex: 'ref_score',
      key: 'ref_score',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Position',
      dataIndex: 'position',
      key: 'position',
    },
  ];

  const siteAlterationRawScores = useMemo(() => {
    if (
      spliceSiteAlterationPrediction &&
      spliceSiteAlterationPrediction?.raw_scores
    ) {
      return spliceSiteAlterationPrediction?.raw_scores;
    }
    return [];
  }, [spliceSiteAlterationPrediction]);

  /**
   * Handles updating In silico prediction data for the current variant
   * Fetches the latest In silico prediction data and updates the Redux store
   */
  const handleUpdateMainDataClick = async () => {
    // Prevent multiple simultaneous updates
    if (isMainTableLoading) {
      return;
    }
    try {
      if (params.vid) {
        const data = await updateSilicoMainData({
          variant_id: params.vid,
        }).unwrap();

        // Update both the prediction data and versioning info
        if (data) {
          notification.success({
            message: 'Success!',
            description: 'In silico prediction data updated successfully',
          });
        }
      }
    } catch (error) {
      console.log(error);
      notification.error({
        message: 'Error',
        description: 'Failed to update In silico prediction data',
      });
    }
  };

  const handleUpdateSliceAlterationDataClick = async () => {
    if (isSliceAlterationTableLoading) {
      return;
    }

    try {
      if (params.vid) {
        const data = await updateSilicoSpliceSiteAlteration({
          variant_id: params.vid,
        }).unwrap();

        if (data.raw_scores?.length > 0) {
          notification.success({
            message: 'Success!',
            description: 'Splice site alteration data updated',
          });
          // TODO: We should make sure that api returns message instead of array of errors
        } else if (data.errors) {
          notification.warning({
            message: 'No valid data received from the API.',
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <DetailCard>
      <CollapsePanel
        title={
          <div className="section-header-with-action">
            <CollapseHeaderForSections title="In silico prediction" />
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              className="clinical-obs__add-proband-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsToolModalVisible(true);
              }}
              disabled={isNewToolDisabled}
            >
              New tool
            </Button>
          </div>
        }
        panelKey="silico-prediction"
        defaultActiveKey="silico-prediction"
      >
        {/* UCSC Linkout under the title of the section */}
        {linkouts?.insilico_ucsc_linkout && (
          <div className="interpretation__intervar-link">
            <a
              href={linkouts.insilico_ucsc_linkout}
              target="_blank"
              rel="noopener noreferrer"
              className="interpretation__intervar-link__anchor"
            >
              UCSC
            </a>
          </div>
        )}

        <div className="molecular-impact">
          <ResizableTable
            columns={columns}
            dataSource={!isExpanded ? baseData : expandedData}
            pagination={false}
            size="small"
            rowKey={(record) => record.key}
            loading={isMainTableLoading}
            persistenceKey="silico-prediction"
          />
          {isExpanded && (
            <div style={{ marginLeft: '400px' }}>
              <ResizableTable
                columns={spliceColumns}
                dataSource={siteAlterationRawScores}
                pagination={false}
                size="small"
                style={{ marginTop: '16px' }}
                loading={isSliceAlterationTableLoading}
                persistenceKey="silico-splice-scores"
              />
            </div>
          )}
          <div className="molecular-impact__silico-prediction-footer">
            <div>
              <VersionHistoryFooter
                historyInfo={`${silicoPredictionVersioningInfo.source_version ? silicoPredictionVersioningInfo.source_version : DATA_SOURCE_FALLBACKS.ENSEMBL} ${silicoPredictionVersioningInfo.date ? `${silicoPredictionVersioningInfo.date}` : ''}`}
                onUpdateClick={handleUpdateMainDataClick}
                styles={{ paddingBottom: '4px' }}
                disabled={isUpdateDisabled}
              />

              <VersionHistoryFooter
                historyInfo={`${
                  spliceSiteAlterationPrediction?.versioning_info
                    ?.source_version
                    ? spliceSiteAlterationPrediction?.versioning_info
                        ?.source_version
                    : DATA_SOURCE_FALLBACKS.BROAD
                } ${
                  spliceSiteAlterationPrediction?.versioning_info?.date
                    ? spliceSiteAlterationPrediction?.versioning_info?.date
                    : ''
                }`}
                styles={{ marginTop: '0px', paddingTop: '5px' }}
                onUpdateClick={handleUpdateSliceAlterationDataClick}
                disabled={isUpdateDisabled}
              />
            </div>
            <LoadMoreBtn
              showMore={isExpanded}
              handleShowMoreClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
        </div>
      </CollapsePanel>

      {/* Modal to ADD a prediction tool entry for this variant (always added tools appear under category "Functional") */}
      {params.vid && (
        <SilicoToolModal
          variant_id={params.vid}
          open={isToolModalVisible}
          closeModal={handleToolModalClose}
        />
      )}

      {/* Modal to EDIT an existing tool entry; opened by clicking an underlined tool name in the table */}
      {isToolEditModalVisible && selectedTool && params.vid && (
        <SilicoPredictionToolEditModal
          variant_id={params.vid}
          open={isToolEditModalVisible}
          onCancel={handleToolEditModalClose}
          tool={selectedTool}
        />
      )}
    </DetailCard>
  );
};

export default SilicoPredictionSection;
